import React, { useEffect, useMemo } from 'react';

import { Box, Typography, styled } from '@mui/material';

import { TAG_TYPE_PASSPORT, TagTypeValue } from '../constants';
import { useTagAssignmentsForForm } from '../hooks/useTagAssignmentsForForm';
import { SectionStyled as BaseSectionStyled, SectionContentStyled, SectionHeaderStyled } from '../styles';

import TagAssignmentsDestination from './TagAssignmentsDestination/TagAssignmentsDestination';
import TagAssignmentsExperience from './TagAssignmentsExperience/TagAssignmentsExperience';
import TagAssignmentsHotel from './TagAssignmentsHotel/TagAssignmentsHotel';

export type EntityType = 'HOTEL' | 'EXPERIENCE' | 'DESTINATION';

export interface AssignmentFormData {
  entityType: EntityType;
  entityId: string;
}

const SectionStyled = styled(BaseSectionStyled)(({ theme }) => ({
  '&:not(:last-child)': {
    marginBottom: theme.spacing(4),
  },
}));

interface Props {
  tagId?: string;
  assignments: Array<AssignmentFormData>;
  tagType: TagTypeValue;
  onAssignmentsChange: (assignments: Array<AssignmentFormData>) => void;
}

export default function TagAssignments({ tagId, assignments, tagType, onAssignmentsChange }: Props) {
  const tagAssignmentsData = useTagAssignmentsForForm({
    tagId,
  });
  const savedAssignments = tagAssignmentsData.assignments;
  const savedAssignmentsPersisted = tagAssignmentsData.savedAssignments;
  const isPassportTag = tagType === TAG_TYPE_PASSPORT;

  useEffect(() => {
    if (tagId && savedAssignments.length > 0 && assignments.length === 0) {
      onAssignmentsChange(savedAssignments);
    }
  }, [tagId, savedAssignments, assignments.length, onAssignmentsChange]);

  const assignmentsByType = useMemo(() => {
    const grouped = {
      HOTEL: assignments.filter((a) => a.entityType === 'HOTEL').map((a) => a.entityId),
      EXPERIENCE: assignments.filter((a) => a.entityType === 'EXPERIENCE').map((a) => a.entityId),
      DESTINATION: assignments.filter((a) => a.entityType === 'DESTINATION').map((a) => a.entityId),
    };
    return grouped;
  }, [assignments]);

  const handleHotelChange = (selectedOfferIds: Array<string>) => {
    const otherAssignments = assignments.filter((a) => a.entityType !== 'HOTEL');
    const hotelAssignments: Array<AssignmentFormData> = selectedOfferIds.map((offerId) => ({
      entityType: 'HOTEL',
      entityId: offerId,
    }));
    onAssignmentsChange([...otherAssignments, ...hotelAssignments]);
  };

  const handleExperienceChange = (selectedOfferIds: Array<string>) => {
    const otherAssignments = assignments.filter((a) => a.entityType !== 'EXPERIENCE');
    const experienceAssignments: Array<AssignmentFormData> = selectedOfferIds.map((offerId) => ({
      entityType: 'EXPERIENCE',
      entityId: offerId,
    }));
    onAssignmentsChange([...otherAssignments, ...experienceAssignments]);
  };

  const handleDestinationChange = (selectedPlaceIds: Array<string>) => {
    const savedDestinationIds = tagId
      ? savedAssignmentsPersisted.filter((a) => a.entityType === 'DESTINATION').map((a) => a.entityId)
      : [];

    const allDestinationIds = [
      ...savedDestinationIds,
      ...selectedPlaceIds.filter((id) => !savedDestinationIds.includes(id)),
    ];

    const otherAssignments = assignments.filter((a) => a.entityType !== 'DESTINATION');
    const destinationAssignments: Array<AssignmentFormData> = allDestinationIds.map((placeId) => ({
      entityType: 'DESTINATION',
      entityId: placeId,
    }));
    onAssignmentsChange([...otherAssignments, ...destinationAssignments]);
  };

  return (
    <Box>
      <SectionStyled>
        <SectionHeaderStyled>
          <Typography component="h2" variant="h4" fontWeight="bold">
            Tagged destinations
          </Typography>
          {assignmentsByType.DESTINATION.length > 0 && (
            <Typography variant="body2" ml={2} color="text.secondary">
              ({assignmentsByType.DESTINATION.length} selected)
            </Typography>
          )}
        </SectionHeaderStyled>
        <SectionContentStyled>
          <TagAssignmentsDestination
            tagId={tagId}
            selectedPlaceIds={assignmentsByType.DESTINATION}
            onSelectionChange={handleDestinationChange}
          />
        </SectionContentStyled>
      </SectionStyled>
      <SectionStyled>
        <SectionHeaderStyled>
          <Typography component="h2" variant="h4" fontWeight="bold">
            Tagged hotels
          </Typography>
          {assignmentsByType.HOTEL.length > 0 && (
            <Typography variant="body2" ml={2} color="text.secondary">
              ({assignmentsByType.HOTEL.length} selected)
            </Typography>
          )}
        </SectionHeaderStyled>
        <SectionContentStyled>
          <TagAssignmentsHotel
            tagId={tagId}
            selectedOfferIds={assignmentsByType.HOTEL}
            onSelectionChange={handleHotelChange}
            savedOfferIds={savedAssignmentsPersisted.filter((a) => a.entityType === 'HOTEL').map((a) => a.entityId)}
          />
        </SectionContentStyled>
      </SectionStyled>
      <SectionStyled>
        <SectionHeaderStyled>
          <Typography component="h2" variant="h4" fontWeight="bold">
            Tagged experiences
          </Typography>
          {assignmentsByType.EXPERIENCE.length > 0 && (
            <Typography variant="body2" ml={2} color="text.secondary">
              ({assignmentsByType.EXPERIENCE.length} selected)
            </Typography>
          )}
        </SectionHeaderStyled>
        <SectionContentStyled>
          <TagAssignmentsExperience
            tagId={tagId}
            voucherBasedOnly={isPassportTag}
            selectedOfferIds={assignmentsByType.EXPERIENCE}
            onSelectionChange={handleExperienceChange}
            savedOfferIds={savedAssignmentsPersisted
              .filter((a) => a.entityType === 'EXPERIENCE')
              .map((a) => a.entityId)}
          />
        </SectionContentStyled>
      </SectionStyled>
    </Box>
  );
}
